import { useEffect, useState } from 'react';
import { Button, Select, Spinner, Tag, makeStyles } from '@fluentui/react-components';
import { useNavigate } from 'react-router-dom';

import { adminListTenants, logout } from '../api/auth.js';
import { useTenantSwitch } from '../lib/useTenantSwitch.js';
import { adminSetTenantPlan, type SimulatedPlan } from '../api/admin.js';
import { useAuthStore } from '../stores/authStore.js';
import { useUsageStore } from '../stores/usageStore.js';
import { UsageMeter } from '../components/UsageMeter.js';
import { AppTopBar } from '../components/AppTopBar.js';
import { Breadcrumbs } from '../components/Breadcrumbs.js';
import { AdminUsageSimulator } from '../components/AdminUsageSimulator.js';
import { TiendaDialog } from '../components/TiendaDialog.js';
import { formatBytes } from '../api/storage.js';
import { tokensPct, monthRemaining } from '../api/usage.js';
import { CreditosChip } from '../components/CreditosChip.js';
import { NOTA_EQUIVALENCIA, enCreditos, etiquetaCreditos, formatCreditos, promediosDe } from '../lib/creditos.js';
import {
  approxFromRemaining,
  fallbackPlanEquivalence,
} from '../lib/planEquivalence.js';
import { nombreProductoActivo } from '../lib/brandingDelProducto.js';

const fmtCompact = new Intl.NumberFormat('es-MX', {
  notation: 'compact',
  maximumFractionDigits: 1,
});

const useStyles = makeStyles({
  page: {
    minHeight: '100vh',
    display: 'grid',
    gridTemplateRows: 'auto auto 1fr',
  },
  topbar: {
    height: 'var(--topbar-h)',
    display: 'flex',
    alignItems: 'center',
    padding: '0 var(--space-xxl)',
    gap: 'var(--space-l)',
    backdropFilter: 'blur(20px) saturate(1.8)',
    backgroundColor: 'rgba(5, 8, 16, 0.75)',
    borderBottom: '1px solid var(--border-subtle)',
    position: 'sticky',
    top: 0,
    zIndex: 100,
  },
  logo: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--space-m)',
  },
  logoMark: {
    width: '32px',
    height: '32px',
    borderRadius: 'var(--radius-md)',
    background: 'linear-gradient(135deg, var(--opus-brand-60), var(--opus-brand-40))',
    display: 'grid',
    placeItems: 'center',
    fontFamily: 'var(--font-display)',
    fontWeight: 700,
    fontSize: '14px',
    color: 'white',
    boxShadow: 'var(--glow-brand)',
  },
  logoText: {
    fontFamily: 'var(--font-display)',
    fontWeight: 600,
    fontSize: 'var(--fs-400)',
  },
  spacer: { flex: 1 },
  tenantBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 'var(--space-s)',
    padding: '6px 12px',
    background: 'var(--bg-raised)',
    border: '1px solid var(--border-default)',
    borderRadius: 'var(--radius-circular)',
    fontSize: 'var(--fs-200)',
    fontFamily: 'var(--font-mono)',
  },
  tenantSlug: {
    color: 'var(--text-tertiary)',
  },
  userChip: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--space-s)',
    padding: '6px 12px',
    fontSize: 'var(--fs-300)',
    fontWeight: 500,
  },
  staffPill: {
    background: 'rgba(245, 158, 11, 0.15)',
    color: 'var(--opus-accent-70)',
    fontFamily: 'var(--font-mono)',
    fontSize: 'var(--fs-100)',
    padding: '2px 8px',
    borderRadius: 'var(--radius-circular)',
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
  },
  content: {
    padding: 'var(--space-4xl) var(--space-xxl)',
    maxWidth: 'var(--content-max)',
    margin: '0 auto',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--space-xxxl)',
  },
  greet: {
    fontFamily: 'var(--font-display)',
    fontSize: 'var(--fs-700)',
    fontWeight: 600,
    margin: 0,
    letterSpacing: '-0.02em',
    lineHeight: 1.1,
  },
  greetAccent: {
    color: '#3b82f6',
  },
  sub: {
    fontSize: 'var(--fs-400)',
    color: 'var(--text-secondary)',
    margin: 0,
    marginTop: 'var(--space-m)',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
    gap: 'var(--space-l)',
  },
  card: {
    background: 'var(--bg-surface)',
    border: '1px solid var(--border-subtle)',
    borderRadius: 'var(--radius-xl)',
    padding: 'var(--space-xxl)',
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--space-m)',
  },
  cardLabel: {
    fontFamily: 'var(--font-mono)',
    fontSize: 'var(--fs-200)',
    color: 'var(--text-tertiary)',
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
  },
  cardTitle: {
    fontFamily: 'var(--font-display)',
    fontWeight: 600,
    fontSize: 'var(--fs-500)',
    margin: 0,
  },
  cardDesc: {
    fontSize: 'var(--fs-300)',
    color: 'var(--text-secondary)',
    margin: 0,
  },
  programRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--space-m)',
    padding: 'var(--space-m) var(--space-l)',
    background: 'var(--bg-raised)',
    borderRadius: 'var(--radius-md)',
  },
  isolation: {
    background: 'var(--bg-surface)',
    border: '1px solid var(--border-subtle)',
    borderRadius: 'var(--radius-xl)',
    padding: 'var(--space-xxl)',
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--space-l)',
  },
  isolationHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 'var(--space-m)',
  },
  isolationResult: {
    background: 'var(--bg-code)',
    fontFamily: 'var(--font-mono)',
    fontSize: 'var(--fs-200)',
    padding: 'var(--space-l)',
    borderRadius: 'var(--radius-md)',
    border: '1px solid var(--border-subtle)',
    overflow: 'auto',
    color: 'var(--text-secondary)',
    whiteSpace: 'pre-wrap',
  },
  switcherList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--space-s)',
  },
  switcherBtn: {
    width: '100%',
    justifyContent: 'flex-start',
  },
  teamsBadge: {
    fontFamily: 'var(--font-mono)',
    fontSize: 'var(--fs-100)',
    color: 'var(--text-tertiary)',
    background: 'var(--bg-raised)',
    border: '1px solid var(--border-subtle)',
    borderRadius: 'var(--radius-circular)',
    padding: '2px 10px',
    marginLeft: 'var(--space-m)',
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
  },
  bigMeter: {
    fontSize: 'var(--fs-500)',
  },
  opsRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: 'var(--space-l)',
  },
  catalogsStat: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    padding: 'var(--space-m) var(--space-l)',
    background: 'var(--bg-raised)',
    borderRadius: 'var(--radius-md)',
  },
  catalogsRow: {
    display: 'flex',
    alignItems: 'baseline',
    gap: 'var(--space-s)',
  },
  catalogsNum: {
    fontFamily: 'var(--font-mono)',
    fontSize: 'var(--fs-500)',
    fontWeight: 700,
  },
  catalogsHint: {
    fontSize: 'var(--fs-200)',
    color: 'var(--text-tertiary)',
    margin: 0,
  },
  vigenciaNote: {
    fontSize: 'var(--fs-200)',
    color: 'var(--text-tertiary)',
    margin: '0 0 var(--space-m)',
    lineHeight: 1.45,
  },
  recargaRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--space-l)',
    flexWrap: 'wrap',
  },
});

function planDisplayName(usage: { plan?: string | null; plan_display?: string | null } | null | undefined): string {
  if (usage?.plan_display?.trim()) return usage.plan_display.trim();
  const m: Record<string, string> = { start: 'Start', pro: 'Pro', teams: 'Teams', vitruvio: 'Vitruvio' };
  if (!usage?.plan) return '—';
  return m[usage.plan] ?? usage.plan;
}

const fmtN = new Intl.NumberFormat('es-MX');

export function DashboardPage() {
  const styles = useStyles();
  const navigate = useNavigate();
  const { user, tenant, programs, availableTenants, clearSession } = useAuthStore(
    (s) => ({
      user: s.user,
      tenant: s.tenant,
      programs: s.programs,
      availableTenants: s.availableTenants,
      clearSession: s.clearSession,
    }),
  );

  const [isolationResult, setIsolationResult] = useState<string | null>(null);
  const [running, setRunning] = useState(false);
  // Mismo cambio de contexto (y misma limpieza) que el selector de la barra
  // superior: los dos pasan por `useTenantSwitch`.
  const { switchingSlug, switchTo } = useTenantSwitch();
  const usage = useUsageStore((s) => s.data);
  const usageError = useUsageStore((s) => s.error);
  const refreshUsage = useUsageStore((s) => s.refresh);
  const [planDraft, setPlanDraft] = useState<SimulatedPlan>('start');
  const [planApplying, setPlanApplying] = useState(false);
  const [planResult, setPlanResult] = useState<string | null>(null);
  const [rechargeOpen, setRechargeOpen] = useState(false);

  useEffect(() => {
    if (usage?.plan) setPlanDraft(usage.plan);
  }, [usage?.plan]);

  useEffect(() => {
    if (!tenant) return;
    void refreshUsage();
  }, [tenant, refreshUsage]);

  if (!user || !tenant) return null;

  async function onLogout() {
    await logout();
    clearSession();
    navigate('/login', { replace: true });
  }

  async function runIsolationProbe() {
    setRunning(true);
    setIsolationResult(null);
    try {
      const data = await adminListTenants();
      setIsolationResult(JSON.stringify(data, null, 2));
    } catch (err) {
      setIsolationResult(`ERROR: ${(err as Error).message}`);
    } finally {
      setRunning(false);
    }
  }

  async function onSwitch(t: { slug: string; name: string; kind?: 'team' | 'company' }) {
    const ok = await switchTo(t);
    if (ok) setIsolationResult(null);
  }

  async function applyPlan() {
    if (!tenant) return;
    setPlanApplying(true);
    setPlanResult(null);
    try {
      const r = await adminSetTenantPlan(tenant.id, planDraft);
      setPlanResult(`Plan actualizado a "${r.simulatedPlan}" para ${tenant.name}.`);
      await refreshUsage();
    } catch (err) {
      setPlanResult(`ERROR: ${(err as Error).message}`);
    } finally {
      setPlanApplying(false);
    }
  }

  const supernova = programs.find((p) => p.programId === 'supernova');
  const otherTenants = availableTenants.filter((t) => t.slug !== tenant.slug);

  return (
    <div className={styles.page}>
      <AppTopBar />
      <Breadcrumbs items={[{ label: 'Inicio', to: '/projects' }, { label: 'Panel' }]} />

      <main className={styles.content}>
        <div>
          <h1 className={styles.greet}>
            Hola, <span className={styles.greetAccent}>{user.name.split(' ')[0]}</span>.
          </h1>
          <p className={styles.sub}>
            Sesión activa en <strong>{tenant.name}</strong>. Suscripción{' '}
            <strong>{supernova?.status ?? 'desconocida'}</strong> a {nombreProductoActivo()}.
          </p>
        </div>

        <section className={styles.grid}>
          <div className={styles.card}>
            <div className={styles.cardLabel}>Programas contratados</div>
            <h2 className={styles.cardTitle}>{programs.length} activos</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-s)' }}>
              {programs.map((p) => (
                <div key={p.programId} className={styles.programRow}>
                  <strong style={{ flex: 1 }}>{p.programId}</strong>
                  <Tag
                    size="small"
                    appearance="filled"
                    style={{
                      background:
                        p.status === 'active' ? 'rgba(16,185,129,.15)' : 'rgba(245,158,11,.15)',
                    }}
                  >
                    {p.status}
                  </Tag>
                </div>
              ))}
            </div>
          </div>

          <div className={styles.card}>
            <div className={styles.cardLabel}>Tu rol</div>
            <h2 className={styles.cardTitle}>{tenant.role}</h2>
            <p className={styles.cardDesc}>
              Empresa <strong>{tenant.slug}</strong> · {user.username}
            </p>
          </div>

          {otherTenants.length > 0 && (
            <div className={styles.card}>
              <div className={styles.cardLabel}>Cambiar de contexto</div>
              <h2 className={styles.cardTitle}>{otherTenants.length} disponibles</h2>
              <div className={styles.switcherList}>
                {otherTenants.map((t) => (
                  <Button
                    key={t.slug}
                    appearance="secondary"
                    className={styles.switcherBtn}
                    disabled={switchingSlug !== null}
                    icon={switchingSlug === t.slug ? <Spinner size="tiny" /> : undefined}
                    onClick={() => void onSwitch(t)}
                  >
                    {t.kind === 'team' ? `Equipo ${t.name}` : t.name} · {t.slug}
                  </Button>
                ))}
              </div>
            </div>
          )}
        </section>

        <section className={styles.isolation}>
          <div className={styles.cardLabel}>Consumo del mes</div>
          <h2 className={styles.cardTitle} style={{ marginTop: 4 }}>
            Plan <strong>{planDisplayName(usage)}</strong>
            {usage?.plan === 'teams' && <span className={styles.teamsBadge}>Bolsa compartida</span>}
            {usage?.period && (
              <span
                style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: 'var(--fs-300)',
                  color: 'var(--text-tertiary)',
                  marginLeft: 'var(--space-m)',
                  fontWeight: 400,
                }}
              >
                · {usage.period}
              </span>
            )}
          </h2>
          {usage && (
            <p className={styles.vigenciaNote}>
              Bolsa mensual de {enCreditos(usage) ? 'créditos' : 'tokens'} de {nombreProductoActivo()}. El plan vence con tu OPUS 27.
            </p>
          )}
          <div
            style={{
              marginTop: 8,
              padding: '10px 12px',
              borderRadius: 8,
              background: 'var(--accent-weak, rgba(12,111,214,0.1))',
              border: '1px solid rgba(12,111,214,0.35)',
              fontSize: 'var(--fs-200)',
              color: 'var(--text-primary)',
              lineHeight: 1.45,
            }}
          >
            Periodo educativo: los primeros 60 días medimos tu consumo, no lo cobramos.
          </div>
          {usageError ? (
            <p className={styles.cardDesc}>No se pudo cargar el consumo: {usageError}</p>
          ) : !usage ? (
            <p className={styles.cardDesc}>Cargando consumo…</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-xl)' }}>
              {(() => {
                const pct = tokensPct(usage);
                const rem = monthRemaining(usage);
                const eq = usage.plan_equivalence
                  ?? fallbackPlanEquivalence(usage.plan, usage.seats ?? 1);
                const approx = approxFromRemaining(rem, {
                  concepts: eq.concepts,
                  apus: eq.apus,
                  chats: eq.chats,
                  tokens_month: eq.tokens_month || eq.tokens_week || usage.tokens.quota || 1,
                });
                const packTokens = usage.tokens.pack_tokens ?? 0;
                const cr = enCreditos(usage);
                return (
                  <>
                    {pct >= 75 && (
                      <p style={{
                        margin: 0,
                        padding: '8px 10px',
                        borderRadius: 8,
                        border: `1px solid ${pct >= 90 ? 'var(--bad)' : 'var(--warn)'}`,
                        background: pct >= 90 ? 'rgba(239,68,68,0.1)' : 'rgba(245,158,11,0.1)',
                        fontSize: 'var(--fs-200)',
                      }}>
                        {pct >= 90
                          ? `Vas al ${pct}% de tus ${cr ? 'créditos' : 'tokens'} del mes. Compra una recarga para no quedarte corto.`
                          : `Has usado el ${pct}% de tus ${cr ? 'créditos' : 'tokens'} del mes.`}
                      </p>
                    )}
                    <div className={styles.bigMeter}>
                      {cr ? (
                        <>
                          <UsageMeter
                            label="Créditos del mes"
                            current={Math.floor(usage.tokens.used / 10_000)}
                            limit={Math.max(1, Math.floor((usage.tokens.quota || 1) / 10_000))}
                            unit="créditos"
                          />
                          <p className={styles.catalogsHint} style={{ marginTop: 4 }}>
                            Quedan <CreditosChip variante="solo" />
                            {' · '}≈ {fmtN.format(approx.concepts)} conceptos · {fmtN.format(approx.apus)} APUs · {fmtN.format(approx.chats)} chats (promedios)
                          </p>
                        </>
                      ) : (
                        <>
                          <UsageMeter
                            label="Tokens del mes"
                            current={usage.tokens.used}
                            limit={Math.max(1, usage.tokens.quota || 1)}
                            unit="tokens"
                          />
                          <p className={styles.catalogsHint} style={{ marginTop: 4 }}>
                            Quedan <strong>{fmtCompact.format(rem)}</strong> tokens
                            {' · '}≈ {fmtN.format(approx.concepts)} conceptos · {fmtN.format(approx.apus)} APUs · {fmtN.format(approx.chats)} chats
                          </p>
                        </>
                      )}
                    </div>

                    <div>
                      <div className={styles.cardLabel}>Orden de consumo</div>
                      <p className={styles.catalogsHint} style={{ marginTop: 4 }}>
                        {cr
                          ? `1. Bolsa mensual del plan (${etiquetaCreditos(usage.tokens.quota)}) → 2. Recargas${packTokens > 0 ? ` (+${formatCreditos(packTokens, 'saldo')} créditos disponibles)` : ''}. Las recargas no se pierden al cambiar de mes y no vencen. ${NOTA_EQUIVALENCIA}.`
                          : `1. Bolsa mensual del plan (${fmtCompact.format(usage.tokens.quota)} tokens) → 2. Recargas${packTokens > 0 ? ` (+${fmtCompact.format(packTokens)} tokens disponibles)` : ''}. La recarga no se pierde al cambiar de mes y no vence.`}
                      </p>
                    </div>
                  </>
                );
              })()}

              <div className={styles.opsRow}>
                <div className={styles.catalogsStat}>
                  <div className={styles.catalogsRow}>
                    <span className={styles.cardLabel}>Conceptos (mes)</span>
                    <span className={styles.catalogsNum}>
                      {fmtN.format(usage.counters.concepts_quoted)}
                    </span>
                  </div>
                </div>
                <div className={styles.catalogsStat}>
                  <div className={styles.catalogsRow}>
                    <span className={styles.cardLabel}>APUs (mes)</span>
                    <span className={styles.catalogsNum}>
                      {fmtN.format(usage.counters.apus_generated)}
                    </span>
                  </div>
                </div>
                <div className={styles.catalogsStat}>
                  <div className={styles.catalogsRow}>
                    <span className={styles.cardLabel}>Chats (mes)</span>
                    <span className={styles.catalogsNum}>
                      {fmtN.format(usage.counters.chats_sent)}
                    </span>
                  </div>
                </div>
                <div className={styles.catalogsStat}>
                  <div className={styles.catalogsRow}>
                    <span className={styles.cardLabel}>Proyectos</span>
                    <span className={styles.catalogsNum}>
                      {fmtN.format(usage.counters.projects_created)} de {usage.limits.projects_created != null ? fmtN.format(usage.limits.projects_created) : '∞'}
                    </span>
                  </div>
                </div>
              </div>

              <div className={styles.catalogsStat}>
                <div className={styles.catalogsRow}>
                  <span className={styles.cardLabel}>Catálogos propios</span>
                  <span className={styles.catalogsNum}>
                    {usage.counters.catalogs_active} de {usage.limits.catalogs_active ?? '∞'}
                  </span>
                </div>
                <p className={styles.catalogsHint}>eCostos siempre incluido — no cuenta aquí</p>
              </div>

              {usage.plan === 'teams' && usage.team_breakdown && usage.team_breakdown.length > 0 && (
                <div>
                  <div className={styles.cardLabel}>Desglose por usuario (bolsa compartida)</div>
                  <table style={{ width: '100%', marginTop: 8, fontSize: 'var(--fs-200)', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ textAlign: 'left', color: 'var(--text-tertiary)' }}>
                        <th style={{ padding: '4px 8px' }}>Usuario</th>
                        <th style={{ padding: '4px 8px' }}>Conceptos</th>
                        <th style={{ padding: '4px 8px' }}>APUs</th>
                        <th style={{ padding: '4px 8px' }}>Chats</th>
                      </tr>
                    </thead>
                    <tbody>
                      {usage.team_breakdown.map((u) => (
                        <tr key={u.user_id}>
                          <td style={{ padding: '4px 8px' }}>{u.name || u.email || u.user_id}</td>
                          <td style={{ padding: '4px 8px' }}>{fmtN.format(u.concepts_quoted)}</td>
                          <td style={{ padding: '4px 8px' }}>{fmtN.format(u.apus_generated)}</td>
                          <td style={{ padding: '4px 8px' }}>{fmtN.format(u.chats_sent)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <p className={styles.catalogsHint}>
                    Nota: {enCreditos(usage) ? 'los créditos' : 'los tokens'} del mes son bolsa del equipo; esta tabla muestra actividad atribuida cuando existe.
                  </p>
                </div>
              )}

              <div>
                <div className={styles.cardLabel}>¿Qué consume mi capacidad?</div>
                {enCreditos(usage) ? (
                  <ul className={styles.cardDesc} style={{ margin: '8px 0 0', paddingLeft: 18, lineHeight: 1.5 }}>
                    <li>Las operaciones (cotizar, APUs con IA, chats) consumen <strong>créditos</strong> de tu bolsa mensual. {NOTA_EQUIVALENCIA}.</li>
                    <li>Se cobra el consumo real de cada operación; estos son promedios, no una tarifa fija:</li>
                    {promediosDe(usage)
                      .filter((p) => ['ia_completa', 'apu', 'procedimiento', 'precio_mercado', 'chat'].includes(p.id))
                      .map((p) => (
                        <li key={p.id}>{p.label}: {p.texto}</li>
                      ))}
                    <li>eCostos no cuenta en tu límite de catálogos propios del usuario</li>
                  </ul>
                ) : (
                <ul className={styles.cardDesc} style={{ margin: '8px 0 0', paddingLeft: 18, lineHeight: 1.5 }}>
                  <li>Las operaciones (cotizar, APUs con IA, chats) consumen <strong>tokens</strong> de tu bolsa mensual</li>
                  <li>Equivalencia de referencia: 1M tokens ≈ 300 conceptos · 75 APUs · 20 chats</li>
                  <li>Start 2M/mes ≈ 600 conceptos · 150 APUs · 40 chats; Pro/Teams (por usuario) 5M ≈ 1 500 · 375 · 100; Vitruvio 50M ≈ 15 000 · 3 750 · 1 000</li>
                  <li>≈ {fmtN.format(3300)} tokens por concepto en promedio</li>
                  <li>eCostos no cuenta en tu límite de catálogos propios del usuario</li>
                </ul>
                )}
              </div>

              {usage.storage && (
                <div>
                  <UsageMeter
                    label={usage.storage.scope === 'tenant' ? 'Almacenamiento del equipo' : 'Almacenamiento'}
                    current={Math.round(usage.storage.used_bytes / (1024 * 1024))}
                    limit={Math.max(1, Math.round(usage.storage.quota_bytes / (1024 * 1024)))}
                    unit="MB"
                  />
                  <p className={styles.catalogsHint} style={{ marginTop: 4 }}>
                    {formatBytes(usage.storage.used_bytes)} de {formatBytes(usage.storage.quota_bytes)} — archivos
                    subidos y generados (no es mensual; libera espacio borrando en{' '}
                    <span
                      style={{ cursor: 'pointer', textDecoration: 'underline' }}
                      onClick={() => navigate('/files')}
                    >
                      Mis archivos
                    </span>).
                  </p>
                </div>
              )}
            </div>
          )}
        </section>

        {usage && (
          <section className={styles.isolation}>
            <div className={styles.cardLabel}>Paquetes</div>
            <h2 className={styles.cardTitle} style={{ marginTop: 4 }}>
              {enCreditos(usage) ? 'Recarga de créditos' : 'Recarga de tokens'}
            </h2>
            <div className={styles.recargaRow}>
              <p className={styles.cardDesc} style={{ margin: 0 }}>
                Primero se gasta la bolsa mensual; luego {enCreditos(usage) ? 'los créditos' : 'los tokens'} de recarga. Las recargas no
                se pierden al cambiar de mes y no vencen. Sin renovación automática · sin tope de compras.
              </p>
              <Button appearance="primary" onClick={() => setRechargeOpen(true)}>
                {enCreditos(usage) ? 'Comprar créditos' : 'Comprar tokens'}
              </Button>
            </div>
          </section>
        )}

        {tenant.isEcosoftOwned && (tenant.role === 'admin' || tenant.role === 'owner') && (
          <section className={styles.isolation}>
            <div className={styles.cardLabel}>Pruebas internas (staff ECOSOFT)</div>
            <h2 className={styles.cardTitle} style={{ marginTop: 4 }}>
              Simular plan del tenant actual
            </h2>
            <p className={styles.cardDesc}>
              Cambia <code>tenants.simulated_plan</code> para probar los meters y
              el enforcement de cuotas. Mecanismo provisional hasta que Anywhere
              provea el plan real del cliente.
            </p>
            <div
              style={{
                display: 'flex',
                gap: 'var(--space-m)',
                alignItems: 'center',
                marginTop: 'var(--space-m)',
              }}
            >
              <Select
                value={planDraft}
                onChange={(_, d) => setPlanDraft(d.value as SimulatedPlan)}
                disabled={planApplying}
                style={{ minWidth: 180 }}
              >
                <option value="start">Start</option>
                <option value="pro">Pro</option>
                <option value="teams">Teams (bolsa compartida)</option>
                <option value="vitruvio">Vitruvio (50M · individual)</option>
              </Select>
              <Button
                appearance="primary"
                onClick={() => void applyPlan()}
                disabled={planApplying || planDraft === usage?.plan}
                icon={planApplying ? <Spinner size="tiny" /> : undefined}
              >
                {planApplying ? 'Aplicando…' : 'Aplicar'}
              </Button>
              {planResult && (
                <span
                  style={{
                    fontSize: 'var(--fs-200)',
                    color: planResult.startsWith('ERROR')
                      ? 'var(--bad)'
                      : 'var(--text-secondary)',
                  }}
                >
                  {planResult}
                </span>
              )}
            </div>

            <div style={{ marginTop: 'var(--space-xl)', paddingTop: 'var(--space-l)', borderTop: '1px solid var(--border-subtle)' }}>
              <div className={styles.cardLabel}>Panel de pruebas (QA)</div>
              <h3 className={styles.cardTitle} style={{ marginTop: 4, fontSize: 'var(--fs-400)' }}>
                Forzar estado de consumo
              </h3>
              <p className={styles.cardDesc} style={{ marginBottom: 'var(--space-m)' }}>
                Presets de un click o counters manuales (ej. <code>chats=29</code> para probar el
                último mensaje permitido). Aplica al tenant actual y refresca la UI al instante.
              </p>
              <AdminUsageSimulator tenantId={tenant.id} tenantName={tenant.name} />
            </div>
          </section>
        )}

        {/* Sonda interna: imprime la respuesta cruda de un endpoint de administración.
            Estaba a la vista de CUALQUIER cliente en su pantalla de cuenta, con la ruta
            del API y el JSON tal cual. Va detrás de staff, como el resto de las pruebas
            internas de esta pantalla. */}
        {tenant.isEcosoftOwned && (tenant.role === 'admin' || tenant.role === 'owner') && (
        <section className={styles.isolation}>
          <div className={styles.isolationHeader}>
            <div>
              <div className={styles.cardLabel}>Pruebas internas (staff ECOSOFT)</div>
              <h2 className={styles.cardTitle} style={{ marginTop: 8 }}>
                Sonda de aislamiento multi-empresa
              </h2>
              <p className={styles.cardDesc} style={{ marginTop: 8 }}>
                <code>GET /api/v1/admin/tenants</code> devuelve{' '}
                <strong>solo las empresas donde eres miembro</strong>{' '}
                (aislamiento estricto).
              </p>
            </div>
            <Button
              appearance="primary"
              onClick={() => void runIsolationProbe()}
              disabled={running}
              icon={running ? <Spinner size="tiny" /> : undefined}
            >
              {running ? 'Probando…' : 'Probar'}
            </Button>
          </div>
          {isolationResult && <pre className={styles.isolationResult}>{isolationResult}</pre>}
        </section>
        )}
      </main>
      <TiendaDialog open={rechargeOpen} onClose={() => setRechargeOpen(false)} />
    </div>
  );
}
